import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient.js';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [authLoading, setAuthLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Cek session di background & listen auth changes
  useEffect(() => {
    let mounted = true;

    const checkUserSession = async (session) => {
      if (!session) return;

      if (mounted) {
        setAuthLoading(true); // Tampilkan loading saat memproses sesi
      }

      try {
        const user = session.user;

        // Cek profile di tabel profiles
        let { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          // Lanjut coba upsert aja kalau error bukan karena koneksi fatal
        }

        // Jika profile belum ada, buat otomatis
        if (!profile) {
          const email = user.email || user.user_metadata?.email || null;
          const username =
            user.user_metadata?.full_name ||
            (email ? email.split('@')[0] : 'User');

          // Upsert profile baru tanpa nomor HP
          const { data: newProfile, error: upsertError } = await supabase
            .from('profiles')
            .upsert(
              {
                id: user.id,
                email: email,
                username: username,
                // phone tidak diisi (biarkan null jika diizinkan DB)
                role: 'user', // default role
              },
              { onConflict: 'id' }
            )
            .select()
            .single();

          if (upsertError) {
            throw upsertError;
          }
          profile = newProfile;
        }

        // Redirect based on role
        if (mounted && profile) {
          const role = profile.role || 'user';
          if (role === 'admin') {
            navigate('/dashboardadmin');
          } else {
            navigate('/dashboard');
          }
        }
      } catch (err) {
        console.error('Error processing session:', err);
        if (mounted) {
          setMessage({
            type: 'error',
            text: 'Gagal memproses login: ' + err.message,
          });
          setAuthLoading(false);
        }
      }
    };

    // 1. Cek session awal
    supabase.auth.getSession().then(({ data: { session } }) => {
      checkUserSession(session);
    });

    // 2. Listen perubahan auth (misal setelah redirect login Google)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      checkUserSession(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Mulai login Google
  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/login',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google Login Error:', error);
      setMessage({
        type: 'error',
        text: 'Gagal memulai login Google: ' + error.message,
      });
      setAuthLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-8 font-sans">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">

        {/* Full Width Header Image */}
        <div className="w-full h-48 bg-gray-50 relative">
          <img
            src="/assets/dashboard/login.png"
            alt="Login Illustration"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content Area with Padding */}
        <div className="p-6">
          <div className="text-center w-full mb-6">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-1 tracking-tight">
              {t('login.title')}
            </h2>
            <p className="text-gray-500 text-sm">
              {t('login.description')}
            </p>
          </div>

          {message && (
            <div
              className={`mb-6 p-4 rounded-xl text-sm flex items-start gap-3 border ${message.type === 'error'
                ? 'bg-red-50 text-red-700 border-red-100'
                : 'bg-blue-50 text-blue-700 border-blue-100'
                }`}
            >
              <span className="text-lg">
                {message.type === 'error' ? '🚫' : 'ℹ️'}
              </span>
              <span className="font-medium">{message.text}</span>
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={authLoading}
            className="group relative w-full flex justify-center items-center py-3.5 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-[#2299ED] hover:bg-[#1fa2f2] active:bg-[#1c90d9] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2299ED] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {authLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('login.redirecting')}
              </>
            ) : (
              <>
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <div className="bg-white p-1 rounded-full group-hover:scale-110 transition-transform">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_google)">
                        <path d="M23.52 12.2727C23.52 11.4545 23.4545 10.6909 23.3273 9.95453H12V14.5454H18.4909C18.2073 16 17.3455 17.2636 16.0582 18.0909V20.9818H19.9309C22.2 18.9272 23.52 15.8636 23.52 12.2727Z" fill="#4285F4" />
                        <path d="M12.0001 24C15.2401 24 17.9674 22.9363 20.0074 21.0545L16.091 18.0909C15.011 18.8181 13.6256 19.2545 12.0001 19.2545C8.87464 19.2545 6.22918 17.1454 5.28009 14.309H1.27649V17.4181C3.37104 21.5727 7.67464 24 12.0001 24Z" fill="#34A853" />
                        <path d="M5.28001 14.2909C5.04001 13.5636 4.9091 12.7909 4.9091 12C4.9091 11.2091 5.04001 10.4363 5.28001 9.70907V6.59998H1.27637C0.425464 8.29089 -0.000171661 10.1091 -0.000171661 12C-0.000171661 13.8909 0.425464 15.7091 1.27637 17.4L5.28001 14.2909Z" fill="#FBBC05" />
                        <path d="M12.0001 4.74545C13.7619 4.74545 15.3437 5.35454 16.5874 6.54545L20.0728 3.05454C17.962 1.09091 15.2346 0 12.0001 0C7.67464 0 3.37104 2.42727 1.27649 6.59998L5.28009 9.70907C6.22918 6.87272 8.87464 4.74545 12.0001 4.74545Z" fill="#EA4335" />
                      </g>
                      <defs>
                        <clipPath id="clip0_google">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                </span>
                <span className="ml-2 font-medium">{t('login.googleBtn')}</span>
              </>
            )}
          </button>

          <p className="mt-8 text-xs text-center text-gray-400">
            By clicking continue, you agree to our <a href="#" className="underline hover:text-blue-600">Terms of Service</a> and <a href="#" className="underline hover:text-blue-600">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>

  );
};

export default LoginPage;
